/**
 * Individuals Service
 * TypeScript functions that call the Python API
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';
import {
  Individual,
  CreateIndividualRequest,
  UpdateIndividualRequest,
  IndividualFilters,
} from '../types/individual';
import { PaginatedResponse } from '../types/api';

// Re-export Individual type for convenience
export type { Individual };

/**
 * Get list of individuals with optional filtering and pagination
 */
export async function getIndividuals(
  filters?: IndividualFilters
): Promise<PaginatedResponse<Individual>> {
  const response = await apiGet<Individual[]>('/individuals', filters);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch individuals error:', { filters, error: response.error });
    throw new Error(serverMsg || 'Unable to load clients. Please try again.');
  }

  return response as PaginatedResponse<Individual>;
}

/**
 * Get a single individual by ID
 */
export async function getIndividualById(id: number): Promise<Individual> {
  const response = await apiGet<Individual>(`/individuals/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch individual error:', { id, error: response.error });
    throw new Error(serverMsg || 'Unable to load client details. Please try again.');
  }

  return response.data;
}

/**
 * Get a single individual by ClientID (user-facing ID like 'C000031')
 */
export async function getIndividualByClientId(clientId: string): Promise<Individual> {
  const response = await apiGet<Individual>(`/individuals/by-client/${clientId}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch individual by client ID error:', { clientId, error: response.error });
    throw new Error(serverMsg || 'Unable to load client details. Please try again.');
  }

  return response.data;
}

/**
 * Get composite individual details by ClientID
 * First resolves clientID to individualID, then fetches full details
 */
export async function getIndividualDetailsByClientId(clientId: string): Promise<{
  individual: Individual;
  cansEligibility: {
    isEligible: boolean;
    isMandatory: boolean;
    reason?: string;
    ageGroup?: string;
    age?: number;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cansAssessments: any[];
}> {
  // First, get the individual to retrieve their individualID
  const individual = await getIndividualByClientId(clientId);

  // Then fetch the full details using the individualID
  if (!individual.individualID) {
    throw new Error('Individual has no ID');
  }
  return getIndividualDetailsComposite(individual.individualID);
}

/**
 * Get composite individual details including CANS eligibility and assessments
 * Returns individual, CANS eligibility, and CANS assessments in one response
 * This is faster than making 3 separate API calls
 */
export async function getIndividualDetailsComposite(id: number): Promise<{
  individual: Individual;
  cansEligibility: {
    isEligible: boolean;
    isMandatory: boolean;
    reason?: string;
    ageGroup?: string;
    age?: number;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cansAssessments: any[];
}> {
  const response = await apiGet<{
    individual: Individual;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cansEligibility: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cansAssessments: any[];
  }>(`/individuals/${id}/details`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load client details. Please try again.');
  }

  return response.data;
}

/**
 * Create a new individual
 */
export async function createIndividual(data: CreateIndividualRequest): Promise<Individual> {
  const response = await apiPost<Individual>('/individuals', data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to create client. Please try again.');
  }

  return response.data;
}

/**
 * Update an existing individual
 */
export async function updateIndividual(
  id: number,
  data: UpdateIndividualRequest
): Promise<Individual> {
  const response = await apiPut<Individual>(`/individuals/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update client. Please try again.');
  }

  return response.data;
}

/**
 * Delete (soft delete) an individual
 */
export async function deleteIndividual(id: number): Promise<void> {
  const response = await apiDelete<null>(`/individuals/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to delete client. Please try again.');
  }
}

/**
 * Search individuals by name or client ID
 */
export async function searchIndividuals(
  searchQuery: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<Individual>> {
  return getIndividuals({ searchQuery, page, pageSize });
}

/**
 * Get active individuals only
 */
export async function getActiveIndividuals(
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<Individual>> {
  return getIndividuals({ isActive: true, page, pageSize });
}

/**
 * Get individuals by zip code
 */
export async function getIndividualsByZipCode(
  zipCode: string,
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<Individual>> {
  return getIndividuals({ zipCode, page, pageSize });
}

/**
 * Get individuals in service area (WeCare Grant eligible)
 * Uses batch endpoint for efficient parallel querying
 * @param serviceAreaZips - Array of zip codes in the service area (fetched from config)
 * @param page - Page number
 * @param pageSize - Number of items per page
 */
export async function getServiceAreaIndividuals(
  serviceAreaZips: string[],
  page: number = 1,
  pageSize: number = 20
): Promise<PaginatedResponse<Individual>> {
  const zipCodes = serviceAreaZips.join(',');

  // Use batch endpoint - single API call instead of 4 sequential calls!
  const response = await apiGet<PaginatedResponse<Individual>>('/individuals/by-zip-codes', {
    zipCodes,
    page,
    pageSize,
  });

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to load service area clients. Please try again.');
  }

  return response.data;
}
